import { useMemo, useState } from "react";
import classNames from "clsx";
import { ensureString, normalizarChassi, normalizarNfe } from "./loteUtils";

const statusToClass = {
  aguardando: "neutral",
  pendente: "neutral",
  lendo: "neutral",
  lido: "info",
  pronto: "success",
  sucesso: "success",
  concluido: "success",
  concluído: "success",
  finalizado: "success",
  done: "success",
  erro: "danger",
  error: "danger",
  falha: "danger",
  failed: "danger",
  processando: "warning",
  em_andamento: "warning",
  ignorado: "warning",
  ignored: "warning",
};

const statusToLabel = {
  aguardando: "Aguardando",
  pendente: "Pendente",
  lendo: "Processando",
  lido: "Lido",
  pronto: "Pronto",
  sucesso: "Sucesso",
  concluido: "Concluído",
  concluído: "Concluído",
  erro: "Erro",
  falha: "Falha",
  failed: "Falha",
  processando: "Processando",
  em_andamento: "Em andamento",
  finalizado: "Finalizado",
  done: "Concluído",
  success: "Sucesso",
  error: "Erro",
  ignorado: "Ignorado",
  ignored: "Ignorado",
};

const statusToVariant = {
  sucesso: "success",
  success: "success",
  concluido: "success",
  concluído: "success",
  pronto: "success",
  finalizado: "success",
  done: "success",
  lido: "success",
  erro: "error",
  error: "error",
  falha: "error",
  failed: "error",
  rejeitado: "error",
  invalido: "error",
  inválido: "error",
  ignorado: "warning",
  ignored: "warning",
};

const resolveVariant = (statusKey) => statusToVariant[statusKey] || "pending";

const resolveProcessNumber = (item = {}) =>
  item.ve101_num_processo ||
  item.processNumber ||
  item.processCode ||
  item.processo ||
  item.processoDetran ||
  item.emplacar?.ve101_num_processo ||
  item.raw?.ve101_num_processo ||
  item.raw?.emplacar?.ve101_num_processo;

const resolveOriginPrefix = (origin) => {
  if (!origin) {
    return null;
  }
  const normalized = origin.toLowerCase();
  if (normalized.includes("detra")) {
    return "DETRANPR";
  }
  return "SISTEMA";
};

const PENDING_STATUSES = new Set([
  "",
  "aguardando",
  "pending",
  "pendente",
  "processando",
  "em_andamento",
  "em andamento",
  "in_progress",
  "lendo",
  "lido",
  "reading",
  "ready",
  "pronto",
  "neutral",
]);

const PROCESSED_FALLBACK_STATUSES = new Set([
  "sucesso",
  "success",
  "erro",
  "error",
  "falha",
  "failed",
  "concluido",
  "concluído",
  "ignored",
  "ignorado",
]);

const DEFAULT_IGNORED_MESSAGE = "Registro existente já possui processo criado no DETRAN.";

const normalizeIdentifiers = (entry = {}) => {
  const lookupNfe =
    entry.nfe ||
    entry.nf ||
    entry.nfeChave ||
    entry.nfe_key ||
    entry.referencia ||
    entry.reference ||
    entry.chave ||
    entry.chaveNfe ||
    entry.chave_nf ||
    entry.ve101_key_nfe;
  const lookupChassi =
    entry.chassi ||
    entry.chassis ||
    entry.ve101_dsc_chassi ||
    entry.veiculo?.chassi ||
    entry.vehicleChassi;
  const normalizedNfe = normalizarNfe(lookupNfe);
  const normalizedChassi = normalizarChassi(lookupChassi);
  const clientId = ensureString(
    entry.clientId ||
      entry.client_id ||
      entry.clientKey ||
      entry.clienteId ||
      entry.cliente_id ||
      entry.entryId ||
      entry.entry_id ||
      entry.payloadId ||
      entry.payload_id ||
      entry.id ||
      entry._id,
  ).trim();
  const id = ensureString(entry.id || entry._id).trim();
  const jobItemKey = ensureString(
    entry.jobItemKey ||
      entry.job_item_key ||
      entry.jobItemId ||
      entry.job_item_id ||
      entry.itemKey ||
      entry.key,
  ).trim();
  const veiculoKey = ensureString(
    entry.ve101_key_public ||
      entry.veiculoKey ||
      entry.veiculo_key ||
      entry.veiculoId ||
      entry.veiculo_id ||
      entry.veiculoKeyPublic ||
      entry.veiculoPublicKey ||
      entry.ve101KeyPublic,
  ).trim();

  return {
    nfe: normalizedNfe || "",
    chassi: normalizedChassi || "",
    clientId,
    id,
    jobItemKey,
    veiculoKey,
  };
};

const buildTokens = (identifiers) => {
  const tokens = [];
  const add = (value) => {
    if (value) {
      tokens.push(value);
    }
  };
  add(identifiers.id);
  add(identifiers.clientId);
  add(identifiers.jobItemKey);
  add(identifiers.veiculoKey);
  if (identifiers.nfe && identifiers.chassi) {
    add(`${identifiers.nfe}::${identifiers.chassi}`);
  }
  if (identifiers.nfe) {
    add(`nfe:${identifiers.nfe}`);
  }
  if (identifiers.chassi) {
    add(`chassi:${identifiers.chassi}`);
  }
  return tokens;
};

const collectIgnoredMessage = (item = {}) => {
  const parts = [
    item.motivo,
    item.reason,
    item.mensagem,
    item.message,
    item.descricao,
    item.description,
    item.detalhe,
    item.detail,
  ]
    .map((value) => ensureString(value).trim())
    .filter(Boolean);
  return parts.length ? parts.join(" ") : null;
};

const resolveProcessoDetranValue = (item = {}) =>
  ensureString(
    item.processoDetran ||
      resolveProcessNumber(item.ignoredMetadata) ||
      resolveProcessNumber(item),
  ).trim();

const buildIgnoredCardItems = (entries = []) =>
  entries.map((entry, index) => {
    const raw = entry?.raw || entry || {};
    const identifiers = normalizeIdentifiers(raw);
    const baseMessage = entry?.mensagem || collectIgnoredMessage(raw) || DEFAULT_IGNORED_MESSAGE;
    const processoDetran = entry?.processoDetran || resolveProcessNumber(raw);
    const fallbackId =
      entry?.uid ||
      raw.id ||
      raw.uid ||
      raw.ve101_key_public ||
      identifiers.id ||
      identifiers.veiculoKey ||
      raw.nfe ||
      raw.chassi ||
      `ignored-${index}`;

    return {
      ...raw,
      id: fallbackId,
      status: "ignored",
      mensagem: baseMessage,
      processoDetran,
      ignoredMetadata: raw,
      nfe: raw.nfe || raw.nf || raw.referencia || identifiers.nfe,
      chassi: raw.chassi || identifiers.chassi,
    };
  });

const ItemRow = ({
  item,
  itemId,
  expanded,
  onToggle,
  processoDetran: processoDetranProp,
}) => {
  const statusKey = (item.status || "").toLowerCase();
  const variant = resolveVariant(statusKey);
  const cardClassName = classNames("lote-progress-item", `lote-progress-item--${variant}`);
  const statusLabel = statusToLabel[statusKey] || item.status || "Desconhecido";
  const originPrefix = resolveOriginPrefix(item.origemErro);
  const shouldPrefix = originPrefix && variant === "error";
  const prefixMessage = (texto) => (shouldPrefix && texto ? `${originPrefix}: ${texto}` : texto);
  const mainMessage = prefixMessage(item.mensagem || item.message);
  const processoDetran = processoDetranProp || resolveProcessoDetranValue(item);
  const allowDetails = !(variant === "success" || statusKey === "ignored" || statusKey === "ignorado");
  const isExpanded = allowDetails && expanded;

  return (
    <div className={cardClassName}>
      <div className="lote-progress-item__header">
        <div>
          <strong>{item.nfe || item.nf || "Sem NFe"}</strong>
          <span className="lote-progress-item__chassi">{item.chassi || "Sem chassi"}</span>
          {item.arquivo && (
            <span className="lote-progress-item__arquivo">{item.arquivo}</span>
          )}
        </div>
        <span
          className={classNames("lote-progress-item__status", statusToClass[statusKey] || "neutral")}
        >
          {statusLabel}
        </span>
      </div>
      {mainMessage && <div className="lote-progress-item__message">{mainMessage}</div>}
      {processoDetran && (
        <div className="lote-progress-item__processo">
          Processo DETRAN: <strong>{processoDetran}</strong>
        </div>
      )}
      {allowDetails && (
        <div className="lote-progress-item__actions">
          <button type="button" className="btn-link" onClick={() => onToggle(itemId)}>
            {isExpanded ? "Ocultar detalhes" : "Ver detalhes"}
          </button>
        </div>
      )}
      {isExpanded && (
        <div className="lote-progress-item__details">
          {item.erro && <div className="danger-text">{prefixMessage(item.erro)}</div>}
          {item.error && <div className="danger-text">{prefixMessage(item.error)}</div>}
          {item.raw && (
            <div className="lote-progress-item__details-block">
              <strong>Detalhes completos</strong>
              <pre className="lote-progress-item__raw">{JSON.stringify(item.raw, null, 2)}</pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

function LoteProgressPanel({
  aberto,
  onClose,
  resumo,
  itens = [],
  ignorados = [],
  socketAtivo,
  usandoFallback,
}) {
  const [expanded, setExpanded] = useState({});
  const itensComIgnorados = useMemo(() => {
    if (!Array.isArray(ignorados) || ignorados.length === 0) {
      return itens;
    }

    const normalizados = ignorados.map((item, index) => {
      const identifiers = normalizeIdentifiers(item);
      const tokens = buildTokens(identifiers);
      const tokenSet = new Set(tokens);
      const uidCandidate =
        ensureString(
          item.id ||
            item.uid ||
            item.jobItemKey ||
            item.job_item_key ||
            item.ve101_key_public ||
            item.referencia ||
            item.nfe ||
            item.chassi,
        ).trim() || tokens[0];
      const uid = uidCandidate || `ignored-${index}`;

      return {
        uid,
        raw: item,
        identifiers,
        tokens,
        tokenSet,
        processoDetran: resolveProcessNumber(item),
        mensagem: collectIgnoredMessage(item),
      };
    });

    const matchable = normalizados.filter(
      (item) => item.processoDetran && item.tokenSet.size > 0,
    );

    if (!matchable.length) {
      return [...itens, ...buildIgnoredCardItems(normalizados)];
    }

    const used = new Set();
    const ajustados = itens.map((item) => {
      const statusKey = (item.status || "").toLowerCase();
      const identifiers = normalizeIdentifiers(item);
      const itemTokens = buildTokens(identifiers);
      if (!itemTokens.length) {
        return item;
      }
      const match = matchable.find((entry) => {
        if (used.has(entry.uid)) {
          return false;
        }
        return itemTokens.some((token) => entry.tokenSet.has(token));
      });

      if (!match) {
        return item;
      }

      used.add(match.uid);

      if (!PENDING_STATUSES.has(statusKey)) {
        return {
          ...item,
          processoDetran: match.processoDetran || item.processoDetran,
          ignoredMetadata: match.raw || item.ignoredMetadata,
        };
      }

      return {
        ...item,
        status: "ignored",
        mensagem: match.mensagem || item.mensagem || DEFAULT_IGNORED_MESSAGE,
        processoDetran: match.processoDetran || item.processoDetran,
        ignoredMetadata: match.raw || item.ignoredMetadata,
      };
    });

    const restantes = normalizados.filter((item) => !item.processoDetran || !used.has(item.uid));

    return [...ajustados, ...buildIgnoredCardItems(restantes)];
  }, [ignorados, itens]);

  const toggle = (id) => {
    setExpanded((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const totals = useMemo(() => {
    const total = resumo?.total ?? itensComIgnorados.length;
    const processed =
      resumo?.processed ??
      itensComIgnorados.filter((item) =>
        PROCESSED_FALLBACK_STATUSES.has((item.status || "").toLowerCase()),
      ).length;
    const success =
      resumo?.success ??
      itensComIgnorados.filter((item) => {
        const statusLower = (item.status || "").toLowerCase();
        return statusLower === "sucesso" || statusLower === "success";
      }).length;
    const failed =
      resumo?.failed ??
      itensComIgnorados.filter((item) => {
        const statusLower = (item.status || "").toLowerCase();
        return statusLower === "erro" || statusLower === "falha" || statusLower === "error" || statusLower === "failed";
      }).length;
    const percentual = total > 0 ? Math.min(100, Math.round((processed / total) * 100)) : 0;
    return { total, processed, success, failed, percentual };
  }, [itensComIgnorados, resumo]);

  if (!aberto) {
    return null;
  }

  const handleOverlayClick = () => {
    onClose?.();
  };

  const stopPropagation = (event) => {
    event.stopPropagation();
  };

  return (
    <div className="lote-progress-overlay" onClick={handleOverlayClick}>
      <aside className="lote-progress-panel" onClick={stopPropagation}>
      <div className="lote-progress-panel__header">
        <div>
          <h3>Progresso do lote</h3>
          <p className="lote-progress-panel__sub">
            {socketAtivo && "Atualização em tempo real"}
            {!socketAtivo && usandoFallback && "Conexão instável. Usando polling."}
            {!socketAtivo && !usandoFallback && "Aguardando atualizações..."}
          </p>
        </div>
        <button type="button" className="btn-icon" onClick={() => onClose?.()}>
          <span className="material-symbols-outlined">close</span>
        </button>
      </div>

      <div className="lote-progress-panel__summary">
        <div className="progress-bar">
          <div
            className="progress-bar__fill"
            style={{ width: `${totals.percentual}%` }}
          />
        </div>
        <ul>
          {resumo?.jobKey && (
            <li>Job: <strong>{resumo.jobKey}</strong></li>
          )}
          <li>Total: <strong>{totals.total}</strong></li>
          <li>Processados: <strong>{totals.processed}</strong></li>
          <li>Sucesso: <strong>{totals.success}</strong></li>
          <li>Falha: <strong>{totals.failed}</strong></li>
          {resumo?.status && (
            <li>Status do job: <strong>{resumo.status}</strong></li>
          )}
        </ul>
      </div>

      <div className="lote-progress-panel__list">
        {itensComIgnorados.length === 0 && (
          <div className="lote-progress-panel__empty">
            Aguarde a atualização dos itens processados.
          </div>
        )}
        {itensComIgnorados.map((item) => {
          const itemId =
            item.id ||
            item.ve101_key_public ||
            item.jobItemKey ||
            item.nfe ||
            item.chassi;
          const processoDetran = resolveProcessoDetranValue(item);
          return (
            <ItemRow
              key={itemId}
              item={item}
              itemId={itemId}
              expanded={!!expanded[itemId]}
              onToggle={toggle}
              processoDetran={processoDetran}
            />
          );
        })}
      </div>
      </aside>
    </div>
  );
}

export default LoteProgressPanel;
